import { useCallback, useRef } from 'react'

import { F0Box, F0Dialog, useF0Form } from '@factorialco/f0-react'
import { ResourceHeader, Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  MyTrainingRequestActivityDocument,
  TrainingRequestDetailFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FromCatalog } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailSidepanelComponent/FromCatalog'
import { NewProposal } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailSidepanelComponent/NewProposal'
import { getStatusLabel, getStatusVariant } from 'modules/trainings/lib/utils'

type Data = {
  id: string
}

export const MyTrainingRequestActivity: ActivityComponent<Data> = ({ id }) => {
  const { open, closeActivity } = useActivity()

  const { loading: loadingRequests, data } = useQuery(MyTrainingRequestActivityDocument, {
    variables: { id },
    skip: !open,
  })

  const trainingRequest = data?.trainings.trainingRequestsConnection.nodes.find(
    (tr) => tr.id === id
  )

  if (!open) return null

  if (loadingRequests) {
    return (
      <F0Dialog
        isOpen
        position="right"
        title={i18n.t('trainings.training_requests.my_requests.detail.title')}
        onClose={closeActivity}
        module={{ id: 'my_trainings', label: i18n.t('trainings.training_requests.header.title') }}
      >
        <Spinner />
      </F0Dialog>
    )
  }

  if (!trainingRequest) return null

  return (
    <TrainingRequestDialog
      trainingRequest={trainingRequest}
      onClose={closeActivity}
    />
  )
}

// --- Inner dialog that owns useF0Form ---
// This component mounts fresh each time the activity opens,
// ensuring the formRef is properly connected to the F0Form instance.

const TrainingRequestDialog = ({
  trainingRequest,
  onClose,
}: {
  trainingRequest: TrainingRequestDetailFragment
  onClose: () => void
}) => {
  const isDraft = trainingRequest.status === 'draft'
  const requestType = trainingRequest.training?.id ? 'fromCatalog' : 'newRequest'

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()
  const setSendForApprovalRef = useRef<((value: boolean) => void) | null>(null)

  const handleFormReady = useCallback(
    (helpers: { setSendForApproval: (value: boolean) => void }) => {
      setSendForApprovalRef.current = helpers.setSendForApproval
    },
    []
  )

  const handleSave = useCallback(() => {
    setSendForApprovalRef.current?.(false)
    submit()
  }, [submit])

  const handleSendForApproval = useCallback(() => {
    setSendForApprovalRef.current?.(true)
    submit()
  }, [submit])

  const createdAtFormatted = trainingRequest.createdAt
    ? dateToString(moment(trainingRequest.createdAt), 'ui')
    : ''

  const getPrimaryAction = () => {
    if (!isDraft) return undefined
    return {
      label: i18n.t('trainings.training_requests.my_requests.detail.send_for_approval'),
      onClick: handleSendForApproval,
      disabled: isSubmitting || hasErrors,
      loading: isSubmitting,
    }
  }

  const getSecondaryAction = () => {
    if (!isDraft) return undefined
    return {
      label: i18n.t('common.save'),
      onClick: handleSave,
      disabled: isSubmitting || hasErrors,
      loading: isSubmitting,
    }
  }

  return (
    <F0Dialog
      isOpen
      position="right"
      title={i18n.t('trainings.training_requests.my_requests.detail.title')}
      onClose={onClose}
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
      module={{ id: 'my_trainings', label: i18n.t('trainings.training_requests.header.title') }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        <ResourceHeader
          title={trainingRequest.trainingName ?? ''}
          status={{
            label: '',
            text: getStatusLabel(trainingRequest.status),
            variant: getStatusVariant(trainingRequest.status),
          }}
          metadata={[
            {
              label: i18n.t('trainings.training_requests.my_requests.detail.created'),
              value: { type: 'text', content: createdAtFormatted },
            },
          ]}
        />
        {requestType === 'fromCatalog' ? (
          <FromCatalog
            trainingRequest={trainingRequest}
            isDraft={isDraft}
            formRef={formRef}
            onSuccess={onClose}
            onFormReady={handleFormReady}
          />
        ) : (
          <NewProposal
            trainingRequest={trainingRequest}
            isDraft={isDraft}
            formRef={formRef}
            onSuccess={onClose}
            onFormReady={handleFormReady}
          />
        )}
      </F0Box>
    </F0Dialog>
  )
}
